<script setup lang="ts">
import { computed, ref } from 'vue';
import { Lightbulb, Sparkles } from 'lucide-vue-next';

// 胶水集成：使用 shadcn-vue 组件构建描述输入 UI
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

/**
 * 奇穴使用方法描述组件
 * @description 使用 Textarea 组件优化使用说明输入体验
 */

interface Props {
  modelValue: string;
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const isFocused = ref(false);

const description = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value)
});

const charCount = computed(() => description.value.length);
const maxLength = 200;

const isOverLimit = computed(() => charCount.value > maxLength);
const isNearLimit = computed(() => charCount.value > maxLength * 0.9 && charCount.value <= maxLength);

const exampleText = `例如：主要治疗手段是「梅花三弄」套盾后打「羽」消除落梅DEBUFF使得能够再次套盾，如此反复。

套盾3秒内免控免封缴，盾破则提前结束。面对「渊冰」、尚未出手的「八卦洞玄」等，可频繁给自身「盾+羽」来获得无限免控以应对。中间插入其他技能则会出现免控真空期。hot有空了补就行，压力大优先盾羽。

面对霸刀【绝唱】可换【乐情】。面对封缴【兼山】可换【捣衣】。面对纯外功配置【流音】可换【敛意】。面对花间【流音】可换【一指回鸾】，利用【石间意】免封缴驱散兰摧。`;

const insertExample = () => {
  description.value = exampleText;
};

defineExpose({
  validate: () => description.value.trim().length >= 10
});
</script>

<template>
  <div class="space-y-4">
    <!-- 标题栏 -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <Label class="text-sm font-medium">奇穴使用方法</Label>
        <Badge 
          :variant="isOverLimit ? 'destructive' : isNearLimit ? 'secondary' : 'outline'"
          class="text-xs"
        >
          {{ charCount }} / {{ maxLength }}
        </Badge>
      </div>
      <Button
        variant="ghost"
        size="sm"
        class="gap-1.5 text-xs h-7 px-2"
        @click="insertExample"
      >
        <Sparkles class="w-3.5 h-3.5" />
        插入示例
      </Button>
    </div>

    <!-- 输入框 -->
    <div 
      class="relative group"
      :class="[
        'rounded-lg border-2 transition-all duration-200',
        isFocused ? 'border-primary ring-2 ring-primary/20' : 'border-border/60',
        isOverLimit ? 'border-destructive ring-2 ring-destructive/20' : ''
      ]"
    >
      <Textarea
        v-model="description"
        :maxlength="maxLength"
        class="min-h-[200px] text-sm resize-y border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent leading-relaxed"
        placeholder="请简要描述这套奇穴的打法思路：

例如：主要治疗手段是「梅花三弄」套盾后打「羽」消除落梅DEBUFF使得能够再次套盾，如此反复。

套盾3秒内免控免封缴，盾破则提前结束。面对「渊冰」、尚未出手的「八卦洞玄」等，可频繁给自身「盾+羽」来获得无限免控以应对。中间插入其他技能则会出现免控真空期。hot有空了补就行，压力大优先盾羽。

面对霸刀【绝唱】可换【乐情】。面对封缴【兼山】可换【捣衣】。面对纯外功配置【流音】可换【敛意】。面对花间【流音】可换【一指回鸾】，利用【石间意】免封缴驱散兰摧。"
        @focus="isFocused = true"
        @blur="isFocused = false"
      />
    </div>

    <!-- 填写建议 -->
    <Alert class="bg-primary/5 border-primary/20">
      <Lightbulb class="w-4 h-4 text-primary" />
      <AlertTitle class="text-sm text-primary">填写建议</AlertTitle>
      <AlertDescription>
        <ul class="mt-2 space-y-1.5 text-xs text-muted-foreground">
          <li class="flex items-start gap-2">
            <span class="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0"></span>
            <span>核心打法思路（技能循环、关键机制）</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0"></span>
            <span>面对不同门派/配置的应对策略</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0"></span>
            <span>奇穴的可替换方案及适用场景</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0"></span>
            <span>操作技巧和注意事项</span>
          </li>
        </ul>
      </AlertDescription>
    </Alert>
  </div>
</template>
